/**
 * Pure scoring functions for the four performance meters.
 *
 * Kept free of React/XState so they are trivially unit-testable. The game machine (machine.ts)
 * calls these to apply per-choice deltas, score the prioritisation pop-up by band-distance, and
 * resolve the graded ending. See docs/game-design.md.
 */
import { DIMENSION_IDS, type DimensionId, type Ending, type GameContent, type PopupScenario, type ScoreDelta } from "@patc/content-schema";

export type Scores = Record<DimensionId, number>;

/** Starting meter values from the content (each dimension's `start`). */
export function initialScores(content: GameContent): Scores {
  const s = {} as Scores;
  for (const d of content.meta.dimensions) s[d.id] = d.start;
  return s;
}

/** Apply a per-dimension delta, clamped to each dimension's [min, max]. */
export function applyDelta(scores: Scores, delta: ScoreDelta, content: GameContent): Scores {
  const next = { ...scores };
  for (const d of content.meta.dimensions) {
    next[d.id] = Math.max(d.min, Math.min(d.max, next[d.id] + (delta[d.id] ?? 0)));
  }
  return next;
}

export interface PopupRankEntry {
  taskId: string;
  rank: number; // 1 = first
}

/** Total positions by which the player's ranking sits outside each task's accepted band. */
export function popupBandDistance(popup: PopupScenario, ranking: PopupRankEntry[]): number {
  const byId = new Map(ranking.map((r) => [r.taskId, r.rank]));
  let dist = 0;
  for (const t of popup.tasks) {
    const r = byId.get(t.id);
    if (r == null) continue;
    const [lo, hi] = t.correctBand;
    if (r < lo) dist += lo - r;
    else if (r > hi) dist += r - hi;
  }
  return dist;
}

/** The pop-up's four-dimension delta: perfect, eroded by penalty per rank-position off-band. */
export function popupDelta(popup: PopupScenario, ranking: PopupRankEntry[]): ScoreDelta {
  const offBy = popupBandDistance(popup, ranking);
  const out = {} as Record<DimensionId, number>;
  for (const d of DIMENSION_IDS) {
    out[d] = Math.round(popup.scoring.perfect[d] + popup.scoring.penaltyPerRankOffBand[d] * offBy);
  }
  return out as ScoreDelta;
}

type Op = Ending["predicate"]["all"][number]["op"];
function compare(value: number, op: Op, target: number): boolean {
  switch (op) {
    case "gte":
      return value >= target;
    case "lte":
      return value <= target;
    case "gt":
      return value > target;
    case "lt":
      return value < target;
  }
}

/** The first ending whose predicate fully matches (in order); falls back to the last ending. */
export function computeEnding(scores: Scores, endings: Ending[]): Ending | null {
  for (const e of endings) {
    if (e.predicate.all.every((c) => compare(scores[c.dimension], c.op, c.value))) return e;
  }
  return endings[endings.length - 1] ?? null;
}
